<script setup>
import { ref, computed } from 'vue'
import EpiSuiteCardTableSection from './components/EpiSuiteCardTableSection.vue'
import { getNonNilArray } from '@/components/combined-predict/models/epiSuite/components/EpiSuiteCard/utils'
import { defaultVTooltipProps } from '@/utils/defaultProps'

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  elevation: {
    type: Number,
    default: 0,
  },
})

const tab = ref(null)

const userGuideLink = computed(() => {
  const helpPage = props.item?.helpPage
  if (!helpPage) return null
  const url = new URL('https://episuite.dev/help/')
  url.searchParams.set('page', helpPage)
  return url.toString()
})
</script>

<template>
  <v-card
    :elevation="elevation"
    class="pa-4 rounded-lg"
    outlined
  >
    <div class="d-flex flex-row justify-content-between align-items-baseline mb-2">
      <div
        class="d-flex flex-row align-items-baseline"
        style="gap: 0.5rem"
      >
        <span
          class="font-weight-bold text-h6 text-primary"
          v-html="item.title"
        />

        <v-tooltip
          v-bind="defaultVTooltipProps"
          z-index="1000"
          top
        >
          <template #activator="{ on, attrs }">
            <v-icon
              v-bind="attrs"
              v-on="on"
              small
              class="cursor-help"
            >
              mdi-information-outline
            </v-icon>
          </template>

          <span v-html="item.description" />
        </v-tooltip>
      </div>

      <v-btn
        v-if="userGuideLink"
        small
        text
        :href="userGuideLink"
        target="_blank"
        class="text-decoration-none user-guide-link"
        aria-label="User Guide link"
      >
        <span>User Guide</span>
        <v-icon
          right
          small
        >
          mdi-information-outline
        </v-icon>
      </v-btn>
    </div>

    <v-tabs
      v-model="tab"
      background-color="transparent"
      height="40"
    >
      <v-tab class="font-weight-bold">Summary</v-tab>

      <v-tab
        v-if="item.modelDescriptors ?? item.model"
        class="font-weight-bold"
      >
        {{ item.modelDescriptors ? 'Model Descriptors' : 'Model' }}
      </v-tab>

      <v-tab
        v-if="item.modelOutput"
        class="font-weight-bold"
      >
        Model Output
      </v-tab>
      <!-- <v-tab class="font-weight-bold">AIM Analogs</v-tab> -->
    </v-tabs>

    <v-divider />

    <v-window v-model="tab">
      <v-window-item transition="fade">
        <v-row class="mt-4">
          <v-col
            v-if="item.summary.values"
            cols="12"
            md="4"
            class="d-flex flex-column text-center pl-4 pr-2"
            style="gap: 1rem"
          >
            <div
              v-for="(summaryValue, i) in getNonNilArray(item.summary.values)"
              :key="i"
              class="d-flex flex-column"
            >
              <div
                class="text-muted"
                v-html="summaryValue.title"
              />

              <div class="font-weight-bold text-h4 text-primary">
                {{ summaryValue.value }}
              </div>

              <div
                v-if="summaryValue?.unit"
                v-html="summaryValue.unit"
              />

              <div
                v-if="summaryValue?.belowUnit"
                v-html="summaryValue.belowUnit"
                class="text-muted mt-1"
              />
            </div>
          </v-col>

          <v-col class="overflow-auto px-1">
            <EpiSuiteCardTableSection
              :tables="item.summary.tables"
              :notes="item.summary.notes"
            />
          </v-col>
        </v-row>
      </v-window-item>

      <v-window-item transition="fade">
        <v-row>
          <v-col
            cols="12"
            md="12"
          >
            <EpiSuiteCardTableSection
              :tables="item.modelDescriptors?.tables ?? item.model?.tables"
              :notes="item.modelDescriptors?.notes ?? item.model?.notes"
            />
          </v-col>
        </v-row>
      </v-window-item>

      <v-window-item
        v-if="item.modelOutput"
        transition="fade"
      >
        <div class="pa-4">
          <pre>{{ item.modelOutput }}</pre>
        </div>
      </v-window-item>

      <!-- <v-window-item transition="fade"><slot name="aim-analogs"></slot></v-window-item> -->
    </v-window>
  </v-card>
</template>

<style scoped>
.v-tabs {
  min-height: 40px;
}

.v-tab {
  text-transform: none !important;
  font-weight: 500;
}

.v-simple-table th {
  font-weight: 600;
  color: #555;
}

.v-tabs ::v-deep .v-tabs-slider-wrapper {
  transition: none;
}

.user-guide-link:hover span {
  text-decoration: underline;
}

.cursor-help:hover {
  cursor: help;
}
</style>
